import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";

import { handleLogin } from "../../../store/user";
import { addCompany } from "../../../store/company";
import { useModal } from "../../../hooks/useModal";

import Header from "./component/notifyPage/header";
import NotifyList from "./component/notifyPage/notifyList";
import Benefit from "./component/notifyPage/benefit";
import TabMenu from "./component/notifyPage/tabMenu";
import DeleteLayer from "./component/notifyPage/deleteLayer";

import { NotifyContainer } from "./component/styled/dashboard";

import Coupon from "./component/notifyPage/coupon";
import { openAdmin } from "./component/function/dashboard";

const NotifyPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector((state) => state?.user?.data[0]);
  const company = useSelector((state) => state?.company?.data);

  const [isSetMenuOpen, setIsSetMenuOpen] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [notification, setNotification] = useState([]);
  const [selectedNotiNos, setSelectedNotiNos] = useState([]);
  const [active, setActive] = useState(0);

  const { modal, openModal, closeModal } = useModal();

  useEffect(() => {
    if (user) {
      getNotification();
    }
  }, [user]);

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    const tabIndex = hash ? parseInt(hash, 10) : null;

    if (tabIndex !== null && !isNaN(tabIndex) && tabIndex < 4) {
      setActive(tabIndex - 1);
    }
    if (JSON.parse(sessionStorage.getItem("coupon"))) {
      sessionStorage.removeItem("coupon");
    }
  }, []);

  // 알림 히스토리 조회
  const getNotification = async (userNo = null) => {
    const url = "/api/notificationHistory";
    const body = {
      mberNo: userNo ? userNo : user.mberNo,
    };
    const res = await axios.post(url, body);
    setNotification(res.data);
  };

  // 선택 삭제 체크박스
  const toggleNotiSelection = (notiNo) => {
    setSelectedNotiNos((selected) => {
      if (selected.includes(notiNo)) {
        return selected.filter((no) => no !== notiNo);
      } else {
        return [...selected, notiNo];
      }
    });
  };

  // 읽음 처리
  const readNotification = async (item, isDelete = false) => {
    if (item.readYn === "N") {
      const url = "/api/notificationHistoryUpdate";
      const body = {
        mberNo: user.mberNo,
        allDelYn: "N",
        allReadYn: "N",
        notiNos: [item.notiNo],
        acceptYn: null,
        gbnValue: null,
        readYn: "Y",
        delYn: isDelete ? "Y" : "N",
      };
      const res = await axios.post(url, body);

      if (res.status === 200) {
        navigateToPage(item);
      }
    } else {
      navigateToPage(item);
    }
  };

  // 페이지 이동
  const navigateToPage = (item) => {
    const navPath = {
      S: () =>
        navigate(`/mypage/detailEvent/${item.refeNo}`, { state: { path: "/mypage/notify" } }),
      R: {
        C: () => openAdmin(user),
        G: () => navigate(`/group/${item.refeNo}`),
      },
      F: () => navigate(`/group/feed`),
      Q: () => inquiryAuthorCheck(item.refeNo),
      RA: () => navigate(`/group/${item.refeNo}`),
    };

    const { notiType, gbn } = item;
    const navFunc = navPath[notiType];

    if (typeof navFunc === "function") {
      navFunc();
    } else if (typeof navFunc === "object" && navFunc[gbn]) {
      navFunc[gbn]();
    }
  };

  const inquiryAuthorCheck = async (refeNo) => {
    if (!refeNo) return;

    const url = "/erp/inquiry";
    const body = { id: refeNo };
    try {
      const res = await axios.post(url, body);
      const data = res.data;

      if (data.mberNo === user.mberNo) {
        navigate("/help");
      } else {
        const cmpny = company.find((cmp) => cmp.cmpnyNo === data.cmpnyNo);
        if (cmpny) {
          openAdmin(user);
        }
      }
    } catch (error) {
      toast.error("해당 문의를 찾을 수 없습니다.");
      console.log(error);
    }
  };

  // 취소 요청 수락, 거절
  const cancelResponse = async (item, acceptYn) => {
    if (!item || !item.refeNo) return;

    const currentOrder = await checkOrder(item.refeNo);
    if (currentOrder.status !== "CANCEL_REQUESTED") {
      toast.error(
        `이미 취소 요청이 ${currentOrder.status === "CANCELLED" ? "수락" : "거절"}된 주문입니다.`,
      );
      closeModal();
      return;
    }
    const updated = await updateCancelOrder(currentOrder, acceptYn);
    if (updated) {
      await readNotification(item, true);
      getNotification();
      toast.success(`취소 요청이 ${acceptYn ? "수락" : "거절"}되었습니다.`);
    }
  };

  // 주문 확인
  const checkOrder = async (id) => {
    const url = "/erp/ss/order";
    const query = `
      query SsOrder($id: ID!) {
        ssOrder(id: $id) {
            id
            cmpnyNo
            status
        }
      }
    `;
    const variables = { id };

    try {
      const res = await axios.post(url, { query, variables });
      return res.status === 200 ? res.data : null;
    } catch (error) {}
  };

  // 주문 상태 업데이트
  const updateCancelOrder = async (order, approve) => {
    const url = "/erp/ss/cancelOrder";
    const body = {
      id: order.id,
      cmpnyNo: order.cmpnyNo,
      approve,
    };

    try {
      const res = await axios.post(url, body);
      if (res.status === 200) {
        closeModal();
        return true;
      }
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  // 초대 수락, 거절
  const inviteResponse = async (item, acceptYn) => {
    if (!item) return;

    const { gbn, refeNo, notiNo } = item;
    const isMember = gbn === "C" && company.some((c) => c.cmpnyNo === refeNo);

    const url = "/api/notificationHistoryUpdate";
    const body = {
      mberNo: user.mberNo,
      allDelYn: "N",
      allReadYn: "N",
      notiNos: [notiNo],
      gbnValue: null,
      readYn: "Y",
      delYn: "Y",
      ...(gbn === "C" && !isMember && { acceptYn: acceptYn ? "Y" : "N" }),
    };

    try {
      const res = await axios.post(url, body);
      if (res.status === 200) {
        toast.success(
          isMember ? "이미 가입된 회사입니다." : `초대가 ${acceptYn ? "수락" : "거절"}되었습니다.`,
        );

        getNotification();

        if (acceptYn) {
          if (gbn === "G") navigate(`/group/${refeNo}`, { state: { display: true } });
          if (gbn === "C") await getCompany(refeNo);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      closeModal();
    }
  };

  // 회사 조회
  const getCompany = async (cmpnyNo) => {
    const url = "/api/cmpny";
    const body = { cmpnyNo: cmpnyNo };

    try {
      const res = await axios.post(url, body);
      const data = res.data;
      console.log(data);

      if (company) {
        dispatch(addCompany(data));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const settingMenuClose = () => {
    setIsDelete(false);
    setIsSetMenuOpen(false);
  };

  // 선택 삭제
  const deleteSelectedNotis = async () => {
    if (!selectedNotiNos.length) {
      toast.error("선택된 알림이 존재하지 않습니다.");
      return;
    }

    const url = "/api/notificationHistoryUpdate";
    const body = {
      mberNo: user.mberNo,
      allDelYn: "N",
      allReadYn: "N",
      notiNos: [...selectedNotiNos],
      acceptYn: null,
      gbnValue: null,
      readYn: "N",
      delYn: "Y",
    };
    const res = await axios.post(url, body);
    if (res.status === 200) {
      toast.success("선택한 알림이 삭제되었습니다.");
      getNotification();
      setSelectedNotiNos([]);
      closeModal();
    }
  };

  // 전체 삭제
  const deleteAllNotis = async () => {
    const url = "/api/notificationHistoryUpdate";
    const body = {
      mberNo: user.mberNo,
      allDelYn: "Y",
      allReadYn: "N",
      notiNos: [],
      acceptYn: null,
      gbnValue: null,
      readYn: "N",
      delYn: "Y",
    };
    const res = await axios.post(url, body);
    if (res.status === 200) {
      toast.success("전체 알림이 삭제되었습니다.");
      getNotification();
      settingMenuClose();
      closeModal();
    }
  };

  // 모두 읽음
  const readAllNotis = async () => {
    const url = "/api/notificationHistoryUpdate";
    const body = {
      mberNo: user.mberNo,
      allDelYn: "N",
      allReadYn: "Y",
      notiNos: [],
      acceptYn: null,
      gbnValue: null,
      readYn: "N",
      delYn: "Y",
    };
    const res = await axios.post(url, body);
    if (res.status === 200) {
      getNotification();
      settingMenuClose();
    }
  };

  const openResponse = (notiItem, acceptYn) => {
    const isInvite = notiItem.notiType === "I";
    const action = acceptYn ? "수락" : "거절";
    const label = isInvite ? "초대" : "취소 요청";

    openModal({
      isOpen: true,
      type: "confirmation",
      title: `${label} ${action}`,
      message: [notiItem.title, `${chooseParticle(label, "을를")} ${action}하시겠습니까?`],
      handleConfirm: () => (isInvite ? inviteResponse : cancelResponse)(notiItem, acceptYn),
      handleCancel: closeModal,
    });
  };

  const tabChange = (idx) => {
    settingMenuClose();
    setActive(idx);

    window.location.hash = `#${idx + 1}`;
  };

  const renderComponent = () => {
    const components = [
      <NotifyList
        notification={notification}
        readNotification={readNotification}
        toggleNotiSelection={toggleNotiSelection}
        selectedNotiNos={selectedNotiNos}
        isDelete={isDelete}
        openResponse={openResponse}
      />,
      <Benefit />,
      <Coupon />,
    ];
    return components[active];
  };

  const tabs = [{ label: "알림" }, { label: "혜택" }, { label: "쿠폰" }];

  return (
    <>
      {modal}
      <NotifyContainer>
        <Header
          isSetMenuOpen={isSetMenuOpen}
          setIsSetMenuOpen={setIsSetMenuOpen}
          settingMenuClose={settingMenuClose}
          isDelete={isDelete}
          setIsDelete={setIsDelete}
          readAllNotis={readAllNotis}
          active={active}
        />
        <div className="inner">
          <TabMenu active={active} tabChange={tabChange} tabs={tabs} className="top" />
          {renderComponent()}
        </div>
        {isDelete && (
          <DeleteLayer
            openModal={openModal}
            deleteSelectedNotis={deleteSelectedNotis}
            closeModal={closeModal}
            deleteAllNotis={deleteAllNotis}
          />
        )}
      </NotifyContainer>
    </>
  );
};

export default NotifyPage;
